import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'none'
  | 'loading'
  | 'error';

export interface QuotaInfo {
  planName: string;
  storageGB: number;
  storageBytes: number;
}

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  accessGranted: boolean;
  trialEnd: string | null;
  trialDaysRemaining: number | null;
  currentPeriodEnd: string | null;
  subscriptionId: string | null;
  quota: QuotaInfo;
}

const DEFAULT_QUOTA: QuotaInfo = {
  planName: 'Sky One',
  storageGB: 50,
  storageBytes: 50 * 1024 * 1024 * 1024,
};

const DEFAULT_STATE: SubscriptionInfo = {
  status: 'loading',
  accessGranted: false,
  trialEnd: null,
  trialDaysRemaining: null,
  currentPeriodEnd: null,
  subscriptionId: null,
  quota: DEFAULT_QUOTA,
};

// Module-level cache — clears when tab closes
let cachedStatus: SubscriptionInfo | null = null;
let cacheUserId: string | null = null;
let cacheTime: number = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function useSubscription() {
  const { userId, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo>(DEFAULT_STATE);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setSubscription({ ...DEFAULT_STATE, status: 'none' });
      return;
    }

    // If returning from payment success page, clear the cache to force a fresh fetch
    const shouldClearCache = sessionStorage.getItem('clearSubscriptionCache');
    if (shouldClearCache) {
      cachedStatus = null;
      cacheUserId = null;
      cacheTime = 0;
      sessionStorage.removeItem('clearSubscriptionCache');
    }

    // Return cached result if still fresh for same user
    const now = Date.now();
    if (cachedStatus && cacheUserId === userId && now - cacheTime < CACHE_TTL_MS) {
      setSubscription(cachedStatus);
      return;
    }

    const fetchStatus = async () => {
      try {
        const url = `${process.env.REACT_APP_GET_USER_STATUS_URL}&userId=${encodeURIComponent(userId)}`;
        const response = await fetch(url);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        const result: SubscriptionInfo = {
          status: data.status,
          accessGranted: data.accessGranted,
          trialEnd: data.trialEnd,
          trialDaysRemaining: data.trialDaysRemaining,
          currentPeriodEnd: data.currentPeriodEnd,
          subscriptionId: data.subscriptionId ?? null,
          quota: data.quota ?? DEFAULT_QUOTA,
        };

        // Cache the result
        cachedStatus = result;
        cacheUserId = userId;
        cacheTime = Date.now();

        setSubscription(result);
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        // Fail open — grant access on error to avoid locking out users
        setSubscription({
          status: 'error',
          accessGranted: true,
          trialEnd: null,
          trialDaysRemaining: null,
          currentPeriodEnd: null,
          subscriptionId: null,
          quota: DEFAULT_QUOTA,
        });
      }
    };

    fetchStatus();
  }, [userId, isAuthenticated]);

  // Force refresh — call after successful payment or subscription change
  const refresh = () => {
    cachedStatus = null;
    cacheUserId = null;
    setSubscription(DEFAULT_STATE);
  };

  return { subscription, refresh };
}
